// @ts-nocheck
import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  addPauseAllSounds,
  addPlayingSoundId,
  addResumeAllSounds,
  howlerSelector,
  removePlayingSound,
  removePlayingSoundId,
  setInitialSounds,
} from '@src/redux/reducer/howler.ts'

import { Howl } from 'howler'
import { store } from '@src/redux/store.ts'

/**
 * playSound 함수는 지정된 이름의 소리를 재생하는 이벤트를 디스패치합니다.
 * @param {string} name - 재생할 소리의 이름
 */
export const playSound = (name: string) => {
  dispatchEvent(new CustomEvent('play-sound', { detail: { name } }))
}
/**
 * stopSound 함수는 지정된 이름의 소리를 정지하는 이벤트를 디스패치합니다.
 * @param {string} name - 정지할 소리의 이름
 */
export const stopSound = (name: string) => {
  dispatchEvent(new CustomEvent('stop-sound', { detail: { name } }))
}
/**
 * stopAllSounds 함수는 모든 소리를 정지하는 이벤트를 디스패치합니다.
 */
export const stopAllSounds = () => {
  dispatchEvent(new Event('stop-all-sounds'))
}
/**
 * muteAllSounds 함수는 모든 소리를 음소거 또는 음소거 해제하는 이벤트를 디스패치합니다.
 * @param {boolean} isMuted - true면 음소거, false면 음소거 해제
 */
export const muteAllSounds = (isMuted: boolean) => {
  dispatchEvent(new CustomEvent('mute-all-sounds', { detail: { isMuted } }))
}

/**
 * SoundHandler 컴포넌트는 애플리케이션의 소리를 관리
 * @param {Object} soundData - 소리 파일 정보를 담고 있는 객체
 */
export default React.memo(function SoundHandler({ soundData = {} }: any) {
  const { playingSounds, pausedSounds, isAllSoundsPaused } = useSelector(howlerSelector)
  const dispatch = useDispatch()

  /** Howl 인스턴스를 초기화하고 이벤트 리스너를 설정 */
  const initHowl = (props) => {
    const howl = new Howl({ ...props, preload: true })
    // 소리가 재생될 때 실행될 콜백 함수를 설정합니다.
    howl.on('play', (id) => {
      const isRemovedBeforePlay = !store.getState().howler.playingSounds[props.name]?.includes(id)
      if (isRemovedBeforePlay) {
        howl.stop(id)
        dispatch(removePlayingSoundId({ name: props.name, id }))
      }
    })
    // 소리가 정지될 때 실행될 콜백 함수를 설정합니다.
    howl.on('stop', (id) => {
      dispatch(removePlayingSoundId({ name: props.name, id }))
    })
    howl.on('pause', (id) => {})
    // 소리가 끝날 때 실행될 콜백 함수를 설정합니다. 루프 설정이 되어 있지 않은 경우에만 실행합니다.
    howl.on('end', (id) => {
      if (howl._loop) return
      dispatch(removePlayingSoundId({ name: props.name, id }))
    })

    howl.on('playerror', () => {
      console.log('에러 발생')
    })
    return howl
  }

  /** soundData를 기반으로 Howl 인스턴스를 생성하고, 이를 객체 형태로 관리합니다. */
  const howlItems = useMemo(() => {
    let object = {}
    soundData.forEach((soundProps) => {
      object[soundProps.name] = initHowl(soundProps)
    })
    return object
  }, [soundData])

  /** 해당 컴포넌트에서 사용할 사운드 초기화 */
  useEffect(() => {
    const initialSounds = {}
    for (let key in howlItems) {
      initialSounds[key] = []
    }
    dispatch(setInitialSounds(initialSounds))
  }, [howlItems])

  /** 사운드 재생, 정지, 일시 정지, 모두 정지, 음소거 처리를 위한 이벤트 리스너를 등록합니다. */
  useEffect(() => {
    const _playSound = ({ detail }) => {
      const sound = howlItems[detail.name]
      const id = sound.play()
      dispatch(addPlayingSoundId({ name: detail.name, id }))
    }
    const _stopSound = ({ detail }) => {
      const sound = howlItems[detail.name]
      sound.stop()
      dispatch(removePlayingSound({ name: detail.name }))
    }
    const _stopAllSounds = () => {
      Object.keys(howlItems).forEach((name) => {
        howlItems[name].stop()
      })
    }
    const _resumeAllSounds = () => {
      if (!isAllSoundsPaused) return
      dispatch(addResumeAllSounds())
      for (let key in pausedSounds) {
        const sound = howlItems[key]
        pausedSounds[key].forEach((id) => sound.play(id))
      }
    }
    const _pauseAllSounds = () => {
      if (isAllSoundsPaused) return
      dispatch(addPauseAllSounds())
      for (let key in playingSounds) {
        const sound = howlItems[key]
        playingSounds[key].forEach((id) => sound.pause(id))
      }
    }
    const _visibilityChange = () => {
      document.visibilityState === 'visible' ? _resumeAllSounds() : _pauseAllSounds()
    }

    const _muteAllSounds = ({ detail }) => {
      Object.keys(howlItems).forEach((name) => howlItems[name].mute(detail.isMuted))
    }

    window.addEventListener('play-sound', _playSound)
    window.addEventListener('stop-sound', _stopSound)
    window.addEventListener('stop-all-sounds', _stopAllSounds)
    window.addEventListener('mute-all-sounds', _muteAllSounds)
    window.addEventListener('visibilitychange', _visibilityChange)

    return () => {
      window.removeEventListener('play-sound', _playSound)
      window.removeEventListener('stop-sound', _stopSound)
      window.removeEventListener('stop-all-sounds', _stopAllSounds)
      window.removeEventListener('mute-all-sounds', _muteAllSounds)
      window.removeEventListener('visibilitychange', _visibilityChange)
    }
  }, [playingSounds, pausedSounds, isAllSoundsPaused])

  /** 컴포넌트가 언마운트될 때 모든 사운드를 정지합니다. */
  useEffect(() => {
    return () => stopAllSounds()
  }, [])

  return null
})
